import { useAppType } from '@dealstack/frontend/shared/mechanism/appTypeContext/appTypeContext';
import { PROJECT } from '@dealstack/shared/lib/common/environment';
import { CommonReduxState } from '@dealstack/frontend/shared/globalConfig';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, generatePath } from 'react-router-dom';
import { useSignOut } from '@dealstack/frontend/shared/mechanism/session/signOut';
import { usePermissionRequest } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper';
import {
	FGAInternalRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { useUserOrganizationId } from '@dealstack/frontend/shared/hooks/useUserOrganizationId/useUserOrganizationId.hook';

export const useUserDropdownV2Controller = () => {
	const [isOpen, setIsOpen] = useState(false);
	const [isSigningOut, setIsSigningOut] = useState(false);
	const signOut = useSignOut();
	const user = useSelector((s: CommonReduxState) => s.user.data);
	const navigate = useNavigate();
	const appType = useAppType();

	const { organizationId: userOrganizationId } = useUserOrganizationId();

	const userSource = user?.person ?? user;

	const [userName, setUserName] = useState('Loading');
	useEffect(() => {
		if (!userSource) {
			setUserName(userName ?? 'Loading');
		} else {
			setUserName(`${userSource.firstName} ${userSource.lastName}`);
		}
	}, [userSource, userName]);

	const isInvestorAppAndProfileNavbarEnabled = appType === PROJECT.INVESTOR;

	const isAdminApp = appType === PROJECT.ADMIN;

	const onToggleUserButton = () => setIsOpen(!isOpen);

	const onClose = () => {
		setIsOpen(false);
	};

	const onNavigate = (path: string) => {
		navigate(path);
		setIsOpen(false);
	};

	const onNavigateToOrganizationManagement = () => {
		if (!userOrganizationId) {
			showToastV2({ state: 'error', message: 'Missing organization id' });
			return;
		}

		onNavigate(
			generatePath(ROUTES.ORGANIZATION_BY_ID, {
				organizationId: userOrganizationId,
			})
		);
	};

	const onNavigateToOrganizationsList = () => {
		onNavigate(ROUTES.ORGANIZATIONS);
	};

	const { hasPermission: hasDealstackSupportPermission } = usePermissionRequest({
		resourceType: ResourceType.DEAL,
		resourceId: '',
		permissionRole: FGAInternalRole.DEALSTACK_SUPPORT,
	});

	const onNavigateToUserProfile = () => {
		onNavigate('/profile');
	};

	const onNavigateToHelpSection = () => {
		onNavigate('/help');
	};

	const onNavigateToExportPage = () => {
		onNavigate('/exports');
	};

	const onNavigateToDocumentTemplates = () => {
		onNavigate(ROUTES.TEMPLATES);
	};

	const onUserLogout = async () => {
		setIsSigningOut(true);
		const signedOut = await signOut();
		setIsOpen(false);

		// On success the loader stays up until the document is replaced.
		if (!signedOut) {
			setIsSigningOut(false);
		}
	};
	return {
		isOpen,
		setIsOpen,
		appType,
		onToggleUserButton,
		onClose,
		onNavigateToUserProfile,
		onNavigateToHelpSection,
		onUserLogout,
		isSigningOut,
		isInvestorAppAndProfileNavbarEnabled,
		userName,
		userSource,
		onNavigateToExportPage,
		onNavigateToDocumentTemplates,
		isAdminApp,
		onNavigateToOrganizationManagement,
		onNavigateToOrganizationsList,
		hasDealstackSupportPermission,
	};
};
